import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Chip,
  Button, Stack, InputAdornment, CircularProgress, Alert
} from '@mui/material';
import { PersonAdd as PersonAddIcon, Save as SaveIcon } from '@mui/icons-material';
import { adminApi } from '../../services/api';

/**
 * Admin card for managing the list of known HyperLink Infrastructure signatories.
 * The list is injected into Claude extraction prompts so any listed name is
 * reliably identified as a HyperLink signatory regardless of document format.
 */
export default function SignatoriesSettings() {
  const [names, setNames] = useState<string[]>([]);
  const [savedNames, setSavedNames] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Load on mount ──────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      try {
        const res = await adminApi.getSettings();
        const row = (res.data as Array<{ key: string; value: string }>)
          .find(s => s.key === 'known_signatories');
        const parsed: string[] = row ? JSON.parse(row.value) : [];
        setNames(parsed);
        setSavedNames(parsed);
      } catch {
        setError('Failed to load signatories');
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  const isDirty = JSON.stringify(names) !== JSON.stringify(savedNames);

  // ── Add a name ─────────────────────────────────────────────────
  const handleAdd = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (names.some(n => n.toLowerCase() === trimmed.toLowerCase())) {
      setInputValue('');
      return; // already in the list
    }
    setNames(prev => [...prev, trimmed]);
    setInputValue('');
  }, [inputValue, names]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  // ── Remove a name ──────────────────────────────────────────────
  const handleDelete = (name: string) => {
    setNames(prev => prev.filter(n => n !== name));
  };

  // ── Save ───────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await adminApi.updateSetting('known_signatories', JSON.stringify(names));
      setSavedNames([...names]);
      setSuccess('Signatories saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to save signatories');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Known HyperLink Signatories
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These names are injected into AI extraction prompts. Any name that appears in a contract —
          in a signature block, approval line, or e-signature record — will be identified as a
          HyperLink signatory rather than the counterparty.
        </Typography>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            {/* Chip list */}
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2, minHeight: 36 }}>
              {names.length === 0 && (
                <Typography variant="body2" color="text.disabled" sx={{ alignSelf: 'center' }}>
                  No signatories configured
                </Typography>
              )}
              {names.map(name => (
                <Chip
                  key={name}
                  label={name}
                  onDelete={() => handleDelete(name)}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Stack>

            {/* Add name input */}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Add a name…"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{ width: 260 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <PersonAddIcon
                        sx={{ fontSize: 18, cursor: 'pointer', color: inputValue.trim() ? 'primary.main' : 'text.disabled' }}
                        onClick={handleAdd}
                      />
                    </InputAdornment>
                  )
                }}
              />
              <Button
                variant={isDirty ? 'contained' : 'outlined'}
                size="small"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!isDirty || saving}
              >
                Save Changes
              </Button>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
